import * as React from "react";
import { useState } from "react";
import styled from "@emotion/styled";
import { Skeleton } from "antd5";
import { Flex, Text } from "styles/utility-components";
import { useLocation } from "wouter";

import { useBidQuestions } from "lib/hooks/api/bid/bid_question/useBidQuestions";
import UIcon from "lib/icons/untitled_ui/UIcon";
import BidQuestionsForm from "./BidQuestionsForm";

export const BID_SELF_SERVE_CHANGED = "BID_SELF_SERVE_CHANGED";

function BidSelfServe(): JSX.Element {
  const { data: allQuestions, isLoading } = useBidQuestions();

  const [hasChanged, setHasChanged] = useState(false);
  // The user has navigated to the self-serve page from a bid report page
  const fromBidQualification =
    window.location.href.includes("bid_qualification") || window.location.href.includes("bids");

  const [location, setLocation] = useLocation();

  return (
    <Page>
      <Header fromBidQualification={fromBidQualification}>
        {fromBidQualification && (
          <BackLink
            onClick={() => {
              if (hasChanged) {
                sessionStorage.setItem(BID_SELF_SERVE_CHANGED, "true");
                setHasChanged(false);
              }
              setLocation(
                location.replace(
                  /\/bid-self-serve\/?$/,
                  window.location.href.includes("bid_qualification") ? "" : "/bid-qualification",
                ),
              );
            }}
          >
            <UIcon icon="arrowNarrowLeft" /> Back to bid / no-bid report
          </BackLink>
        )}
        <Text h1>Bid / no-bid criteria</Text>
        <Description>
          We generate a bid / no-bid report based on default criteria when you upload a tender
          document. You can manage custom criteria below.
          <ListDescription>On this page you can:</ListDescription>
          <List>
            <li>View default criteria</li>
            <li>Add new criteria</li>
            <li>Edit, reorder, or remove custom criteria</li>
          </List>
        </Description>
      </Header>
      {isLoading || !allQuestions ? (
        <SkeletonContainer>
          <Skeleton active />
          <Skeleton active />
        </SkeletonContainer>
      ) : (
        <BidQuestionsForm
          defaultValues={allQuestions.groups}
          fromBidQualification={fromBidQualification}
          hasChanged={hasChanged}
          setHasChanged={setHasChanged}
        />
      )}
    </Page>
  );
}

const Header = styled(Flex)<{ fromBidQualification: boolean }>(({ fromBidQualification }) => ({
  flexDirection: "column",
  gap: 24,
  padding: fromBidQualification ? 48 : 24,
  paddingTop: 24,
  paddingBottom: 0,
}));

const BackLink = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  cursor: "pointer",
  gap: 4,
  color: theme.colors.sysPrimaryDefault,
  "&:hover": {
    textDecoration: "underline",
    color: theme.colors.sysPrimaryDefault,
  },
}));

const Page = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 24,
  overflow: "auto",
  height: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
  width: "-webkit-fill-available",
}));

const Description = styled(Text)({
  maxWidth: 650,
});

const ListDescription = styled(Text)({
  marginTop: 16,
});

const List = styled.ul({
  fontWeight: 500,
  paddingInlineStart: 24,
  marginBlockStart: 8,
});

const SkeletonContainer = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  padding: 16,
  maxWidth: 968,
});

export default BidSelfServe;
